/* eslint-disable no-unused-vars */
/* global G, React */
import { useMemoRef, usePromise, useStyles } from 'platform/react/hook'
import { cloneElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Box, ButtonBase, Grid, useTheme } from '@mui/material'
import { formatFilesize } from 'lib/util/attachment'
import { getReadableDate } from 'lib/util/date'
import { arrayOf } from 'lib/util'
import ApplicationContext from 'platform/react/context/application'
import OverflowTooltip from 'ui/Element/Text/OverflowTooltip'
import MuiListTemplateButton from 'ui/Element/Attachment/List/Template/Button'
import Icon from 'ui/Element/Icon'

const styles = theme => ({
  root: {
    height: '100%',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
  toolbarContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    paddingBottom: 0,
    backgroundColor: theme.palette.background.content,
  },
  toolbar: {
    color: theme.palette.black.main,
    backgroundColor: theme.palette.white.main,
    borderRadius: '0.5rem',
    padding: '0.5rem',
    alignItems: 'center',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    '& > *': {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
  },
  button: {
    width: '2rem',
    height: '2rem',
    backgroundColor: theme.palette.background.content,
    borderRadius: '0.25rem',
    '&:disabled': {
      backgroundColor: theme.palette.white.main,
      '& > *': {
        backgroundColor: theme.palette.border.main,
      },
    },
  },
  navigationButton: {
    backgroundColor: theme.palette.black.main,
  },
  prevButton: {
    '& > *': { // rotating arrow icon
      transform: 'rotate(180deg)',
      cursor: 'pointer',
    },
  },
  previewContainer: {
    height: '100%',
    overflow: 'hidden',
    padding: '1rem',
  },
})

/**
 * Toolbar
 *
 * Shows a toolbar to navigate the attachment group
 *
 * @param {Object} toolbarProps                         props
 * @param {Object} toolbarProps.currentAttachment       the current attachment
 * @param {number} toolbarProps.currentAttachmentIndex  the current attachment index
 * @param {number} toolbarProps.attachmentsCount        number of attachments
 * @param {Object} toolbarProps.classes                 classes from parent component
 * @param {Object} toolbarProps.icons                   icons to show
 * @param {Object} toolbarProps.events                  events to use
 * @param {Object} toolbarProps.props                   additional props
 * @returns {JSX.Element}
 */
const Toolbar = ({
  currentAttachment,
  currentAttachmentIndex,
  attachmentsCount,
  group,
  classes,
  icons,
  events,
  ...props
}) => {
  const theme = useTheme()
  const toolbarRef = useRef()
  const [width, setWidth] = useState(null)

  const title = `${decodeURIComponent(currentAttachment?.value?.name) || false} (${currentAttachmentIndex + 1}/${attachmentsCount})`

  useEffect(() => {
    toolbarRef?.current && setWidth(toolbarRef.current.clientWidth)
  }, [toolbarRef?.current])

  const resizeHandler = () => { setWidth(toolbarRef?.current?.clientWidth) }

  // Recalculating height if we resize the window
  useEffect(() => {
    window.addEventListener('resize', resizeHandler)
    return () => { window.removeEventListener('resize', resizeHandler) }
  }, [])

  const filesize = currentAttachment?.value?.size
    ? formatFilesize(currentAttachment?.value?.size)
    : false

  const created = currentAttachment?.value?.submitTimestamp
    ? getReadableDate(currentAttachment?.value?.submitTimestamp)
    : false

  const nextIcon = (
    <Icon
      source={icons.nextIcon}
      name={'chevron-right'}
      color={'white'}
      background={'black'}
      size={'1.5rem'}
    />
  )

  const previousIcon = (
    <Icon
      source={icons.previousIcon}
      name={'chevron-left'}
      color={theme.palette.white.main}
      size={'1.5rem'}
    />
  )

  return (
    <Grid
      item
      container
      columnGap={1}
      ref={toolbarRef}
      className={classes.toolbar}
    >
      <Grid
        item
        xs={'auto'}
      >
        <ButtonBase
          onClick={events?.onPrevious}
          disabled={currentAttachmentIndex === 0}
          className={`${classes.button} ${classes.navigationButton} ${classes.prevButton}`}
        >
          {previousIcon}
        </ButtonBase>
      </Grid>
      <Grid
        item
        xs={12}
        sm={8}
        sx={{ justifyContent: 'center', maxWidth: `calc(${width}px - 5rem)!important` }}
      >
        {title && (
          <>
            <OverflowTooltip variant={'14/medium'}>
              {title}
            </OverflowTooltip>
            <Box /* Divider (like pipe char) */
              sx={{ width: '1px', height: '2rem', backgroundColor: theme.palette.divider }}
            />
          </>
        )}
        {filesize && (
          <>
            <OverflowTooltip variant={'14/medium'}>
              {filesize}
            </OverflowTooltip>
            <Box /* Divider (like pipe char) */
              sx={{ width: '1px', height: '2rem', backgroundColor: theme.palette.divider }}
            />
          </>
        )}
        {created && (
          <>
            <OverflowTooltip variant={'14/medium'}>
              {created}
            </OverflowTooltip>
            <Box /* Divider (like pipe char) */
              sx={{ width: '1px', height: '2rem', backgroundColor: theme.palette.divider }}
            />
          </>
        )}
        <MuiListTemplateButton
          value={null}
          group={group}
          size={'small'}
          fullWidth={false}
          showProgress={true}
          icon={icons.downloadIcon}
          attachment={currentAttachment}
        />
      </Grid>
      <Grid
        item
        xs={'auto'}
        sx={{ justifyContent: 'flex-end' }}
      >
        <ButtonBase
          onClick={events?.onNext}
          disabled={currentAttachmentIndex === attachmentsCount - 1}
          className={`${classes.button} ${classes.navigationButton}`}
        >
          {nextIcon}
        </ButtonBase>
      </Grid>
    </Grid>
  )
}

/**
 * Attachment Multiple
 *
 * Component to browse through mutiple attachments using {@link AttachmentBig} component. Will display a
 * toolbar at the top with next and previous buttons.
 *
 * @param {Object} multipleProps                    props
 * @param {Object} multipleProps.initialAttachment  the initial attachment to show in the group
 * @param {String} multipleProps.groupName          attachment group name
 * @param {Object} multipleProps.props              additional props
 * @param {Object} multipleProps.props.downloadIcon download button
 * @param {Object} multipleProps.props.nextIcon     arrow right button
 * @param {Object} multipleProps.props.previousIcon arrow left button
 * @returns {JSX.Element}
 */
const AttachmentMultiple = ({ attachment: initialAttachment, group: groupName, ...props }) => {
  const { eventBus } = useContext(ApplicationContext)

  const { downloadIcon, nextIcon, previousIcon } = props

  const [attachments, setAttachments] = useState(null)
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(null)

  useEffect(() => {
    if (attachments) {
      const index = initialAttachment
        ? attachments.findIndex(attachment => attachment.key === initialAttachment.key
          || attachment?.value?.name === initialAttachment.key)
          || 0
        : 0
      setCurrentAttachmentIndex(index)
    }
  }, [attachments])

  const classes = useStyles(styles)()

  useEffect(() => {
    (async () => {
    // listening for the attachment API to set a new list of images to the attachment group
      eventBus.add(eventBus.type(G.ATTACHMENT, G.DONE, groupName), (event) => {
        const newAttachments = event.detail[G.DATA]
        const sortedAttachments = newAttachments.sort((a, b) => new Date(a.value.submitTimestamp) - new Date(b.value.submitTimestamp))
        setAttachments(sortedAttachments)
      }, { once: true })

      // forcing the attachment API to fire its current list of images from groupName (by sending an
      // empty G.DATA object, or the result of `onOpen` handler if available),
      // so that the listener above gets the attachments
      eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, groupName), { [G.DATA]: {
        ...props?.fetch && { [G.DATA]: await props?.events?.onOpen?.(null) || [] },
      } })
    })()
  }, [])

  const handleIncrease = () => {
    setCurrentAttachmentIndex(prevState => prevState + 1)
  }

  const handleDecrease = () => {
    setCurrentAttachmentIndex(prevState => prevState - 1)
  }

  const canShowAttachment = useMemo(() => attachments !== null && Number.isInteger(currentAttachmentIndex), [attachments, currentAttachmentIndex])

  return (
    <Grid
      item
      xs={12}
      container
      className={classes.root}
    >
      <Grid
        item
        container
        className={classes.toolbarContainer}
      >
        {attachments !== null
            && Number.isInteger(currentAttachmentIndex)
            && <Toolbar
              classes={classes}
              group={groupName}
              attachmentsCount={attachments.length}
              currentAttachmentIndex={currentAttachmentIndex}
              icons={{ downloadIcon, nextIcon, previousIcon }}
              currentAttachment={attachments[currentAttachmentIndex]}
              events={{
                onNext: handleIncrease,
                onPrevious: handleDecrease,
              }}
            />}
      </Grid>
      <Grid
        item
        container
        className={classes.previewContainer}
      >
        {arrayOf(props.children).map(child => (
          child.props.hidden || child.key !== 'big' || !canShowAttachment ? null : cloneElement(child, {
            group: groupName,
            attachment: attachments[currentAttachmentIndex],
          })
        ))}
      </Grid>
    </Grid>
  )
}

export default useMemoRef(AttachmentMultiple, props => [
  props.children,
  props.attachment,
  props.group,
])
